import { Component, HostListener, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { Output, ElementRef, EventEmitter } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { BaseComponent, LanguageService, TranslatedToastrService, ValidationHandlerService, DatepartTypeEnum, PivotConfig, PivotDataFieldConfig, FormatService, ExcelExportService, LocalStorageWrapper, ListItemModel } from 'lib/Vestra.shared';
import { PartnerModel, VestraService } from '../../../services/api-services';
declare var $: any;

@Component({
    selector: 'app-user-coupon-report',
    templateUrl: './user-coupon-report.component.html',
    styleUrl: './user-coupon-report.component.scss',
})
export class UserCouponReportComponent extends BaseComponent {
    @ViewChild('pivotContainer', { static: false }) pivotContainer: ElementRef;

    datePickerConfig: Partial<BsDatepickerConfig>;
    datePartEnum = Object.keys(DatepartTypeEnum);
    public datePartTypes: any[];
    public startDate: Date;
    public endDate: Date;

    public showReportPivot: boolean;

    model: any = {
        startDate: Date,
        endDate: Date,
        status: DatepartTypeEnum,
    };
    statusOptions = Object.keys(DatepartTypeEnum);
    dateRange: Date[];
    onDateRangeChange(dateRange: Date[]): void {
        this.model.startDate = dateRange[0];
        this.model.endDate = dateRange[1];

        this.startDate = this.model.startDate as Date;
        this.endDate = this.model.endDate as Date;
    }

    public subscription: Subscription;
    private _languageShortCode: string;
    get languageShortCode() {
        return this._languageShortCode;
    }
    set languageShortCode(value: string) {
        if (this._languageShortCode !== value) {
            this.showReportPivot = false;
            setTimeout(() => {
                if (this.startDate && this.endDate) {
                    this.getDataSource();
                }
            }, 0);
        }

        this._languageShortCode = value;
    }

    @ViewChild('pivot', { read: ElementRef })
    container: ElementRef;

    pivot: any;

    @Output() ready: EventEmitter<any> = new EventEmitter();

    // pivot
    public data: Array<any> = [];
    public pivotAggregatorName: string = 'sum';

    private _pivotValueField: string;
    get pivotValueField(): string {
        return this._pivotValueField;
    }
    set pivotValueField(value: string) {
        this._pivotValueField = value;
        if (this._pivotValueField == '' || !this._pivotValueField) {
            this._pivotValueField = 'couponCount';
        }
    }

    public isShowPivotControls: boolean = true;

    public partnerList: PartnerModel[] = [];
    public redeemTypeList: ListItemModel[] = [];
    public selectedPartnerId: number = 0;
    public selectedPartnerName: string = '';
    public selectedRedeemTypeId: number = 0;


    private translationKeys: Record<string, string> = {
        id: 'common.id',
        partnerName: 'partner.name',
        partnerId: 'partner.id',
        couponTitle: 'coupon.title',
        couponCode: 'coupon.code',
        discountPercentage: 'coupon.discount_percentage',
        discountPrice: 'coupon.discount_price',
        createdDate: 'coupon.created_date',
        expiryDate: 'coupon.expired_date',
        isAdultContent: 'coupon.is_adult_content',
        transactionFee: 'coupon.transaction_fee',
        redeemedAt: 'user_coupon.redeemed_at',
        activatedAt: 'user_coupon.activated_at',
        redeemType: 'coupon.redeem_type',
        cronUserCouponId: 'user_coupon.cron_id',
        firstName: 'vestra_user.first_name',
        lastName: 'vestra_user.last_name',
        vestraUserId: 'vestra_user.vestra_user_id',
        couponCount: 'coupon.count',
        status: 'common.status',
    };

    private keyMap: Record<string, string> = {};
    private labelMap: Record<string, string> = {};

    constructor(
        protected override validationHandlerService: ValidationHandlerService,
        private translateService: TranslateService,
        protected override modalService: BsModalService,
        protected override spinnerService: NgxSpinnerService,
        protected override languageService: LanguageService,
        private formatService: FormatService,
        private excelExportService: ExcelExportService,
        private translatedToastrService: TranslatedToastrService,
        private vestraService: VestraService
    ) {
        super(spinnerService, modalService, validationHandlerService);

        this.datePickerConfig = Object.assign({}, { displayMonths: 1 });

        // default startDate, default endDate
        const today = new Date();
        this.startDate = new Date(today);
        this.startDate.setDate(today.getDate() - 7);
        this.endDate = today;

        this.dateRange = [this.startDate, this.endDate];
    }

    public ngOnInit() {
        this.subscription = this.languageService.language$.subscribe((value: string) => {
            this.languageShortCode = value;
        });

        this.getUserCouponInitData();
        this.getDataSource();
    }

    public ngAfterViewInit(): void {
        setTimeout(() => {
            this.resizePivot();
        });
    }

    public ngOnDestroy() {
        this.pivot && this.pivot.destructor();
        this.subscription.unsubscribe();
    }

    @HostListener('window:resize')
    onResize() {
        this.resizePivot();
    }

    public toggleFields(): void {
        this.isShowPivotControls = !this.isShowPivotControls;

        let selectors = ['.pvtVals.pvtUiCell', '.pvtAxisContainer.pvtHorizList.pvtCols.pvtUiCell', '.pvtAxisContainer.pvtUnused', '.pvtAxisContainer.pvtRows', '.pvtUiCell'];
        selectors.forEach(selector => {
            let elements: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(selector);
            if (elements.length === 0) {
                return;
            }

            let first = elements[0];
            let shouldHide = first.style.display !== 'none';
            elements.forEach(el => {
                el.style.display = shouldHide ? 'none' : '';
            });
        });
    }

    public clearSelection(): void {
        this.selectedPartnerId = 0;
        this.selectedPartnerName = '';
        this.getDataSource();
    }
    public clearRedeemTypeSelection(): void {
        this.selectedRedeemTypeId = 0;
        this.getDataSource();
    }

    public onPartnerChange(event: number): void {
        if(event > 0)
        {
            let currentPartner: PartnerModel | undefined = this.partnerList.find(item => item.id === +event);
            if (!currentPartner) {
                return;
            }
            this.selectedPartnerName = currentPartner.name;
            this.selectedPartnerId = currentPartner.id;
            this.getDataSource(this.selectedPartnerId);
        }
        else
        {
            this.selectedPartnerId = 0;
            this.getDataSource(this.selectedPartnerId);
        }

    }
    public onRedeemTypeChange(event: number): void {
        this.selectedRedeemTypeId = event;
        this.getDataSource(this.selectedPartnerId);
    }

    public onCancel(): void {
        this.loadPivotTable(this.data);
    }

    public onExcelExport = async (): Promise<any> => {
        let pivotConfig = $('#pivot').data('pivotUIOptions');
        if (!pivotConfig) {
            return;
        }

        let rowsKeys = this.translateLabels(pivotConfig.rows);
        let colsKeys = this.translateLabels(pivotConfig.cols);

        let dataFields: PivotDataFieldConfig[] = [{ field: this.pivotValueField, function: this.pivotAggregatorName }];

        let model: PivotConfig = {
            pivotName: 'UserCouponReportPivot',
            rowFields: rowsKeys,
            columnFields: colsKeys,
            dataFields: dataFields,
            data: this.data.map(item => ({
                id: item.id,
                partnerName: item.partnerName,
                partnerId: item.id,
                couponTitle: item.couponTitle,
                couponCode: item.couponCode,
                discountPercentage: item.discountPercentage,
                discountPrice: item.discountPrice,
                createdDate: item.createdDate,
                expiryDate: item.expiryDate,
                isAdultContent: item.isAdultContent,
                transactionFee: item.transactionFee,
                redeemedAt: item.redeemedAt,
                activatedAt: item.activatedAt,
                redeemType: item.redeemType,
                cronUserCouponId: item.cronUserCouponId,
                firstName: item.firstName,
                lastName: item.lastName,
                vestraUserId: item.vestraUserId,
                couponCount: item.couponCount,
                status: item.status,
            })),
        };

        this.excelExportService.exportPivotExcel(model);
    };

    public createReport() {
        this.startDate = this.model.startDate as Date;
        this.endDate = this.model.endDate as Date;
        this.getDataSource(this.selectedPartnerId);
    }

    // private methods
    private resizePivot() {
        if (!this.pivotContainer) {
            return;
        }

        let rect = this.pivotContainer.nativeElement.getBoundingClientRect();
        let viewportHeight = window.innerHeight;
        let availableHeight = viewportHeight - rect.top - 5;

        this.pivotContainer.nativeElement.style.height = availableHeight + 'px';
    }

    private buildFieldLabelMaps(): void {
        this.keyMap = {};
        this.labelMap = {};

        for (let key of Object.keys(this.translationKeys)) {
            let label = this.translateService.instant(this.translationKeys[key]);
            this.keyMap[key] = label;
            this.labelMap[label] = key;
        }
    }

    private translateKeys(keys: string[] = []): string[] {
        // megkeresi az adott kulcsokhoz tartozó labelt (lefordított mezőnév)
        return keys.map(key => this.keyMap[key] || key);
    }

    private translateLabels(labels: string[] = []): string[] {
        // megkeresi az adott labelekhez tartozó kulcsot
        return labels.map(label => this.labelMap[label] || label);
    }

    private loadPivotTable(data: any[]) {
        if (this.selectedPartnerId !== null && this.selectedPartnerId !== 0) {
            for (let i = data.length - 1; i >= 0; i--) {
                if (data[i].partnerName !== this.selectedPartnerName) {
                    data.splice(i, 1);
                }
            }
        }

        $('#pivot').empty();
        this.isShowPivotControls = true;

        const locale = LocalStorageWrapper.getLocale();
        const self = this;


        forkJoin(Object.values(this.translationKeys).map(key => this.translateService.get(key))).subscribe(labels => {
            this.buildFieldLabelMaps();

            const renderers = $.extend($.pivotUtilities.renderers, $.pivotUtilities.plotly_renderers);
            const excludedFromVals = [this.keyMap['couponTitle'], this.keyMap['couponCode'], this.keyMap['discountPercentage'], this.keyMap['discountPrice'], this.keyMap['createdDate'], this.keyMap['expiryDate'], this.keyMap['redeemedAt'], this.keyMap['activatedAt']];

            const renamedDataset = data.length
                ? data.map(item => {
                      let field: any = {};
                      for (let key of Object.keys(this.translationKeys)) {
                          let label = this.keyMap[key];
                          let value = item[key];

                          if (value && ['createdDate', 'expiryDate', 'redeemedAt', 'activatedAt'].includes(key)) {
                              value = this.formatService.formatDateOnly(new Date(value));
                          }

                          field[label] = value ?? '-';
                      }
                      return field;
                  })
                : [{}];

            const defaultConfig = {
                rows: [this.keyMap['id'], this.keyMap['firstName'], this.keyMap['lastName'], this.keyMap['partnerName'], this.keyMap['couponCode'], this.keyMap['redeemType'], this.keyMap['redeemAt'], this.keyMap['activatedAt']],
                cols: [this.keyMap['status']],    
                vals: [this.keyMap['couponCount']],
                aggregatorName: 'Sum',
                rendererName: 'Heatmap',
                renderers: renderers,
                rendererOptions: {
                    table: {
                        clickCallback: function (e: any, value: any, filters: any, pivotData: any) {
                            let names: any[] = [];
                            pivotData.forEachMatchingRecord(filters, function (record: any) {
                                names.push(record.Name);
                            });
                        },
                    },
                },
                onRefresh: (config: any) => self.handlePivotRefresh(config, excludedFromVals),
            };

            $('#pivot').pivotUI(renamedDataset, defaultConfig, true, locale);
        });
    }

    private getDataSource(partnerId?: number) {
        this.showReportPivot = true;
        this.handleSpinner(
            firstValueFrom(this.vestraService.getUserCouponReport(partnerId, this.selectedRedeemTypeId,  this.startDate, this.endDate))
                .then(res => {
                    this.data = res.data;
                    this.loadPivotTable(res.data);
                    return Promise.resolve(res);
                })
                .catch((err: any) => {
                    this.validationHandlerService.handleServerError(err);
                })
        );
    }

    private handlePivotRefresh(config: any, excludedFromVals: string[]): void {
        const self = this;
        const locale = LocalStorageWrapper.getLocale();

        self.pivotValueField = self.labelMap[config.vals[0]];
        self.pivotAggregatorName = self.excelExportService.toCamelCase(config.aggregatorName);

        $('.pvtVals .pvtAttrDropdown option').each(function (this: HTMLElement) {
            const val = $(this).val();
            if (excludedFromVals.includes(val)) {
                $(this).remove();
            }
        });

        let allFields: string[] = [];
        $('#pivot table tr').each((_: any, tr: any) => {
            $(tr)
                .find('.pvtRowLabel')
                .each((i: number, cell: any) => {
                    let fieldName = config.rows[i];
                    if (fieldName && !allFields.includes(fieldName)) {
                        allFields.push(fieldName);
                    }
                });
        });

        // a rowLabel cellákra rárakjuk az aktuálisa propertynek megfelelő data-field attribútumot
        $('#pivot table tr').each((_: any, tr: any) => {
            const $tr = $(tr);
            const $ths = $tr.find('.pvtRowLabel');
            const n = $ths.length;

            $ths.each((i: number, cell: any) => {
                const $cell = $(cell);
                let fieldIndex = allFields.length - n + i;
                if (fieldIndex >= 0 && fieldIndex < allFields.length) {
                    $cell.attr('data-field', allFields[fieldIndex]);
                }
            });
        });

        // value cellákra is rárakjuk a data-field attribútumot
        $('#pivot table tr').each((_: any, tr: any) => {
            $(tr)
                .find('.pvtVal')
                .each((_: any, cell: any) => {
                    const $cell = $(cell);
                    let fieldName = config.vals?.[0];
                    if (fieldName) {
                        $cell.attr('data-field', fieldName);
                    }
                });
        });

        // header
        let axisLabels = $('#pivot table .pvtAxisLabel');
        $('#pivot table tr').each((_: any, tr: any) => {
            const $tr = $(tr);
            $tr.find('.pvtAxisLabel').each((i: number, cell: any) => {
                const $cell = $(cell);
                let fieldName = config.cols?.[axisLabels.index(cell)];
                if (fieldName) {
                    $cell.attr('data-field', fieldName);
                }
            });

            $tr.find('.pvtColLabel').each((i: number, cell: any) => {
                const $cell = $(cell);
                const globalIndex = axisLabels.index($tr.find('.pvtAxisLabel')[0]);
                let fieldName = config.cols?.[globalIndex];
                if (fieldName) {
                    $cell.attr('data-field', fieldName);
                }
            });
        });

        const formatFtFields = (container: JQuery<HTMLElement>, fields: string[]) => {
            container.find('td, th').each(function () {
                let cell = $(this);
                let fieldName = cell.data('field');

                if (fieldName && fields.includes(fieldName)) {
                    let val = cell.text().trim();
                    let rawVal = val.replace(',', '.');
                    if (rawVal !== '' && !isNaN(Number(rawVal))) {
                        let num = parseFloat(rawVal);
                        cell.text(num.toLocaleString('hu-HU', { style: 'currency', currency: 'HUF' }));
                    }
                }
            });
        };

        const formatPercentageFields = (container: JQuery<HTMLElement>, fields: string[]) => {
            container.find('td, th').each(function () {
                let cell = $(this);
                let fieldName = cell.data('field');

                if (fieldName && fields.includes(fieldName)) {
                    let val = cell.text().trim();
                    let rawVal = val.replace(',', '.');
                    if (rawVal !== '' && !isNaN(Number(rawVal))) {
                        let num = parseFloat(rawVal);
                        cell.text(`${num}%`);
                    }
                }
            });
        };

        if (locale === 'en') {
            formatFtFields($('#pivot'), ['Discount Price']);
            formatPercentageFields($('#pivot'), ['Discount Percentage']);
        }

        if (locale === 'hu') {
            formatFtFields($('#pivot'), ['Kedvezményes ár']);
            formatPercentageFields($('#pivot'), ['Kupon kedvezmény százalék']);
        }
    }

    private getUserCouponInitData(): void {
        firstValueFrom(this.vestraService.getUserCouponInitData())
            .then(res => {
                this.partnerList = res.result.partners;
                this.redeemTypeList = res.result.redeemTypes;
                return Promise.resolve(res);
            })
            .catch((err: HttpErrorResponse) => {
                this.validationHandlerService.handleServerError(err);
            });
    }
}
